<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { showErrorMessage } from '@/shared/utils'
import { useOrganizationModules, useReportListing } from '@/shared/composables'
import { ElMessage } from 'element-plus'
import { brushReportApi } from '@/shared/api/report/brushReport.api'
import { userApi } from '@/shared/api'
import BrushMapAside from './components/BrushMapAside/BrushMapAside.vue'
import BrushMapTeeth from './components/BrushMapTeeth.vue'
import BrushMapHeader from './components/BrushMapHeader.vue'
import BrushMapSelected from './components/BrushMapSelected.vue'
import BrushMapModalWindow from './components/BrushMapModalWindow.vue'
import BrushMapPreview from '@/pages/BrushMapPage/components/BrushMapPreview.vue'
import WReportHeader from '@/widgets/WReportHeader/WReportHeader.vue'
import IconTemplate from '@/apps/components/IconTemplate.vue'

const { organizationModules } = useOrganizationModules()

const route = useRoute()
const router = useRouter()

const reportId = computed(() => Number(route.params.reportId))
const patientId = computed(() => Number(route.params.patientId))
const isCreationPage = computed(() => route.params.reportId === '0')
const isPreviewMode = computed(() => route.query['view-mode'] === '1')
const isChangeFirm = ref(false)
const isUpdateReport = ref(false)
const isModalVisible = ref(false)
const isModalQr = ref(false)
const isSmallScreen = ref(false)

const { setUpdateReportListing } = useReportListing()

const firstGroupColors = ref([])
const secondGroupColors = ref([])

const currentColor = ref('#b3b3b3')
const currentName = ref('')

const typeColor = ref()
const deletedColor = ref()
const loading = ref(false)

const teeth = ref()
const firm = ref({
  nameFirm: 'Curaprox',
  firstGroup: 'PRIME',
  secondGroup: 'PERIO',
})

const reportInfo = ref({
  id: reportId,
  date_at: '',
  doctor: undefined,
})

function handleOpenTipDialog() {
  isModalVisible.value = !isModalVisible.value
}

function changeFirm(event) {
  firm.value = event
  isChangeFirm.value = true
}

function changeColor(event) {
  currentColor.value = event.color
  currentName.value = event.colorName
  typeColor.value = event.type
}

function resetColors() {
  firstGroupColors.value = []
  secondGroupColors.value = []
}

const updateColors = function (color, colorName) {
  if (typeColor.value === 1) {
    firstGroupColors.value.push({ color, colorName })
  } else {
    secondGroupColors.value.push({ color, colorName })
  }
}
const deleteCurrentColor = function (color) {
  deletedColor.value = color

  if (firstGroupColors.value.find((item) => item.color === color)) {
    const findObject = firstGroupColors.value.find(
      (item) => item.color === color
    )
    firstGroupColors.value.splice(firstGroupColors.value.indexOf(findObject), 1)
  } else {
    const findObject = secondGroupColors.value.find(
      (item) => item.color === color
    )
    secondGroupColors.value.splice(
      secondGroupColors.value.indexOf(findObject),
      1
    )
  }
}

const resetChangeFirmStatus = () => {
  isChangeFirm.value = false
  currentColor.value = '#b3b3b3'
  currentName.value = 'Выберите цвет'
}

const getUserInfo = async (userId) => {
  const [error, response] = await userApi.getUser(userId)

  if (error) {
    showErrorMessage(error)
  } else {
    reportInfo.value.doctor = response.data.user
  }
}

const sendReport = async () => {
  loading.value = true
  teeth.value.forEach((tooth) => delete tooth.img)

  const report = {
    teeth: teeth.value,
    brushesFirm: firm.value,
    firstGroupColors: firstGroupColors.value,
    secondGroupColors: secondGroupColors.value,
  }

  const [error, response] = await brushReportApi.saveReport(
    patientId.value,
    report
  )

  if (error) {
    showErrorMessage(error)
  } else {
    ElMessage({
      message: 'Отчет сохранен',
      type: 'success',
    })

    setUpdateReportListing(true)
  }

  if (!reportId.value) {
    setUpdateReportListing(true)

    await router.push({
      name: 'BrushMapPage',
      params: {
        patientId: patientId.value,
        reportId: response.data.id,
      },
    })
  }

  loading.value = false
  isModalQr.value = true
}

const updateReport = async () => {
  loading.value = true
  teeth.value.forEach((tooth) => delete tooth.img)

  const report = {
    teeth: teeth.value,
    brushesFirm: firm.value,
    firstGroupColors: firstGroupColors.value,
    secondGroupColors: secondGroupColors.value,
  }

  const [error] = await brushReportApi.updateReport(reportId.value, report)

  if (error) {
    showErrorMessage(error)
  } else {
    ElMessage({
      message: 'Отчет изменён',
      type: 'success',
    })

    isUpdateReport.value = true
    setUpdateReportListing(true)
  }

  loading.value = false
}

const initEmptyBrushData = () => {
  firstGroupColors.value = []
  secondGroupColors.value = []

  isChangeFirm.value = !isChangeFirm.value

  currentColor.value = '#b3b3b3'
  currentName.value = 'Выберите цвет'

  typeColor.value = null
  deletedColor.value = null

  teeth.value = null
  firm.value = {
    nameFirm: 'Curaprox',
    firstGroup: 'PRIME',
    secondGroup: 'PERIO',
  }
}

const getFullReport = async () => {
  loading.value = true

  const [error, response] = await brushReportApi.getReport(reportId.value)

  if (error) {
    showErrorMessage(error)
  } else {
    const userId = response.data.user_id
    firm.value = response.data.brushesFirm
    firstGroupColors.value = response.data.firstGroupColors
    secondGroupColors.value = response.data.secondGroupColors
    await getUserInfo(userId)
  }

  loading.value = false
}

const qrCode = computed(() => {
  let domain = window.origin

  if (process.env.NODE_ENV === 'production') {
    domain = process.env.VUE_APP_BASE_URI
  }

  return `${domain}/api/web/admin/patients/${patientId.value}/get-qr`
})

watch(reportId, (newReportId) => {
  if (route.name === 'BrushMapPage') {
    if (newReportId) {
      getFullReport()
    } else {
      initEmptyBrushData()
    }
  }
})

onMounted(async () => {
  if (window.innerWidth < 1450) {
    isSmallScreen.value = true
  }

  window.addEventListener('resize', () => {
    if (window.innerWidth < 1450) {
      isSmallScreen.value = true
    } else {
      isSmallScreen.value = false
    }
  })
  if (reportId.value) {
    await getFullReport()
  }
})
</script>

<template>
  <div v-loading.body="loading" class="brush-map">
    <div>
      <w-report-header
        v-if="!isCreationPage"
        :report-info="reportInfo"
        report-type="brushMap"
      />
      <div
        :class="{ 'brush-map__block_preview': isPreviewMode }"
        class="brush-map__block"
      >
        <div :class="{ 'brush-map__flex': !isPreviewMode }">
          <brush-map-header
            :firm="firm"
            @open-tip-window="handleOpenTipDialog"
          />
          <div
            :class="{ 'brush-map__content_preview': isPreviewMode }"
            class="brush-map__content"
          >
            <div
              :class="{ 'brush-map__wrapper_preview': isPreviewMode }"
              class="brush-map__wrapper"
            >
              <brush-map-teeth
                :current-color="currentColor"
                :current-name="currentName"
                :deleted-color="deletedColor"
                :first-group-colors="firstGroupColors"
                :is-change-firm="isChangeFirm"
                :is-update-report="isUpdateReport"
                :second-group-colors="secondGroupColors"
                @delete-current-color="deleteCurrentColor($event)"
                @reset-colors="resetColors()"
                @reset-deleted-color="deletedColor = null"
                @reset-change-firm-status="resetChangeFirmStatus()"
                @update-colors="updateColors($event.color, $event.colorName)"
                @update-teeth-data="teeth = $event"
                @update-report="isUpdateReport = false"
              />
            </div>
            <!-- brush-map__wrapper -->
            <brush-map-aside
              v-if="!isPreviewMode"
              :brushes-firm="firm"
              :current-color="currentColor"
              :current-name="currentName"
              :first-group="firm.firstGroup"
              :first-group-colors="firstGroupColors"
              :second-group="firm.secondGroup"
              :second-group-colors="secondGroupColors"
              @resetColors="resetColors()"
              @change-color="changeColor($event)"
              @change-firm="changeFirm($event)"
              @delete-color="deleteCurrentColor($event)"
              @update-report="updateReport()"
              @send-report="sendReport()"
            />
          </div>
          <brush-map-selected
            v-if="isPreviewMode || isSmallScreen"
            :first-group="firm.firstGroup"
            :first-group-colors="firstGroupColors"
            :second-group="firm.secondGroup"
            :second-group-colors="secondGroupColors"
            @delete-color="deleteCurrentColor($event)"
          />
          <div v-if="!isPreviewMode" class="brush-map__buttons">
            <el-button
              v-if="isCreationPage"
              :disabled="organizationModules.indexOf('brushmap') <= -1"
              round
              type="primary"
              @click="sendReport"
            >
              5. Сохранить
            </el-button>
            <el-button
              v-else
              :disabled="organizationModules.indexOf('brushmap') <= -1"
              round
              type="primary"
              @click="updateReport"
            >
              5. Изменить
            </el-button>
          </div>
        </div>
      </div>
      <!-- brush-map__block -->
      <brush-map-preview v-if="isPreviewMode" />
    </div>
    <el-dialog v-model="isModalQr" :show-close="false" width="450">
      <template #header="{ close }">
        <div class="modal__header">
          <p class="modal__title">
            <strong>Дорогой пациент</strong>
            , обязательно
            <br />
            <u>отсканируйте QR-код</u>
            , чтобы получить
            <br />
            карту ёршиков в телеграм!
          </p>
          <icon-template
            class="modal__header-icon"
            height="32"
            name="close"
            width="32"
            @click="close"
          />
        </div>
      </template>
      <template #default>
        <img :src="qrCode" alt="" class="brush-map__qr" />
      </template>
    </el-dialog>
    <brush-map-modal-window
      :is-modal-visible="isModalVisible"
      @close="isModalVisible = false"
    />
  </div>
  <!-- brush-map -->
</template>

<style lang="scss" scoped>
@import '@/apps/styles/resources/index';
@import '@/apps/styles/teeth';

.brush-map {
  display: flex;
  justify-content: center;
  overflow-y: auto;
  padding: 16px 25px 0 18px;
  width: 100%;
  height: 100%;

  &__qr {
    width: 200px;
    height: 200px;
  }

  &__title {
    display: flex;
    justify-content: space-between;
    font-size: 25px;

    &_preview {
      font-size: 30px;
    }

    img {
      cursor: pointer;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__action {
    display: flex;
    cursor: pointer;
    gap: 6px;

    img {
      width: 14px;
      height: 14px;
    }

    p {
      font-family: 'Manrope', sans-serif;
      font-weight: 500;
      font-size: $size--12;
      line-height: 14px;
      color: $color--black;
    }
  }

  &__block {
    margin: 0 auto;
    border-radius: 16px;
    padding: 27px 30px 27px 24px;
    max-width: 1050px;
    background: #fff;

    &_preview {
      display: block;
      border-radius: 0;
      height: 1000px;
      min-width: 800px;

      * {
        pointer-events: none;
      }
    }

    @media screen and (width < 1450px) {
      min-width: 550px;
    }
  }

  &__flex {
    min-width: 600px;
  }

  &__bottom {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  &__content {
    display: flex;
    justify-content: space-between;
    margin-top: 17px;
    width: 100%;
    gap: 25px;

    &_preview {
      justify-content: center;
    }

    @media screen and (width < 1450px) {
      flex-direction: column;
      align-items: center;
    }
  }

  &__wrapper {
    margin-left: 30px;
    width: 450px;
    height: 680px;

    &_preview {
      height: 650px;
    }
  }

  &__buttons {
    display: flex;
    justify-content: center;
    margin-top: 20px;
    width: 100%;

    @media screen and (width >= 1450px) {
      display: none;
    }
  }
}

.modal {
  &__header {
    display: flex;
    justify-content: space-between;

    &-icon {
      margin-right: -10px;
      width: 25px;
      height: 25px;
      cursor: pointer;
    }
  }

  &__title {
    font-family: 'Manrope', sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 25px;
    color: $color--primary;
  }
}
</style>
